import { useMemo, useState } from 'react';
import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { billingKeys, teamMembersKeys, teamsKeys, teamTrialsKeys } from '@data/constants';
import {
  CheckoutView,
  CustomerPortalView,
  ListTeamTrialView,
  TeamApiGetTeamMembersRequest,
  TeamApiGetTeamPanelsRequest,
  TeamApiGetTeamsRequest,
  TeamApiUpdateMemberPanelPermissionsRequest,
  TeamBody,
  TeamDetailsView,
  TeamMemberBody,
  TeamMemberListView,
  TeamMemberView,
  TeamUpdateBody,
  TeamView,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import { APIError } from '@data/utils/types';
import { useDebounce } from '../../../app/utils/hooks/useDebounce.hook';

export const useCreateNewTeam = (): UseMutationResult<TeamView, APIError, TeamBody, unknown> => {
  return useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.team.createTeam({ teamBody: values });
      return response.data;
    },
  });
};

export const useUpdateTeamMembership = (): UseMutationResult<
  TeamMemberView,
  any,
  { teamId: string; userId: string; values: TeamMemberBody },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, userId, values }) => {
      const response = await MatchingApi.team.updateTeamMembership({
        id: teamId,
        userId,
        teamMemberBody: values,
      });
      return response.data;
    },
    onMutate: ({ teamId, userId, values }) => {
      const previousTeamMembers = queryClient.getQueryData<InfiniteData<TeamMemberListView>>(
        teamMembersKeys.list(teamId),
      );

      if (previousTeamMembers) {
        const newPages = previousTeamMembers.pages.map((page: { items: TeamMemberView[] }) => ({
          ...page,
          items: page.items.map((user) =>
            user.userId === userId
              ? {
                  ...user,
                  teamPermissions: values.teamPermissions,
                  trialPermissions: values.trialPermissions,
                  teamRole: values.teamRole,
                  ...(values.panelPermissions !== undefined && {
                    panelPermissions: values.panelPermissions,
                  }),
                }
              : user,
          ),
        }));

        queryClient.setQueryData(teamMembersKeys.list(teamId), {
          ...previousTeamMembers,
          pages: newPages,
        });
      }
      return { previousTeamMembers };
    },
    onError: (_, { teamId }, context) => {
      queryClient.setQueryData(teamMembersKeys.list(teamId), context?.previousTeamMembers);
    },
  });
};

export const useUpdateMemberPanelPermissions = (): UseMutationResult<
  void,
  APIError,
  TeamApiUpdateMemberPanelPermissionsRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestParameters: TeamApiUpdateMemberPanelPermissionsRequest) => {
      await MatchingApi.team.updateMemberPanelPermissions(requestParameters);
    },
    onMutate: ({ id, userId, teamMemberPanelPermissionsBody }) => {
      const previousTeamMembers = queryClient.getQueryData<InfiniteData<TeamMemberListView>>(
        teamMembersKeys.list(id),
      );
      if (previousTeamMembers) {
        const newPages = previousTeamMembers.pages.map((page: { items: TeamMemberView[] }) => ({
          ...page,
          items: page.items.map((user) =>
            user.userId === userId
              ? { ...user, panelPermissions: teamMemberPanelPermissionsBody.panelPermissions }
              : user,
          ),
        }));
        queryClient.setQueryData(teamMembersKeys.list(id), {
          ...previousTeamMembers,
          pages: newPages,
        });
      }
      return { previousTeamMembers };
    },
    onError: (_, requestParameters, context) => {
      if (context?.previousTeamMembers) {
        queryClient.setQueryData(
          teamMembersKeys.list(requestParameters.id),
          context.previousTeamMembers,
        );
      }
    },
    onSettled: (_, __, requestParameters) => {
      queryClient.invalidateQueries({ queryKey: teamMembersKeys.list(requestParameters.id) });
    },
  });
};

export const useTeamDetails = (teamId: string) =>
  useQuery<TeamDetailsView, Error>({
    queryKey: teamsKeys.detail(teamId),
    queryFn: async () => {
      const response = await MatchingApi.team.getTeamDetails({ id: teamId });
      return response.data;
    },
    enabled: !!teamId,
  });

export const useUpdateTeam = (): UseMutationResult<
  TeamView,
  APIError,
  { teamId: string; values: TeamUpdateBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, values }) => {
      const response = await MatchingApi.team.updateTeam({ id: teamId, teamUpdateBody: values });
      return response.data;
    },
    onSuccess: (_, { teamId }) =>
      queryClient.invalidateQueries({ queryKey: teamsKeys.detail(teamId) }),
  });
};

export const useTeamMembers = (teamId: string) => {
  const params = { limit: 20 };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    TeamMemberListView,
    Error
  >({
    queryKey: teamMembersKeys.list(teamId),
    initialPageParam: params,
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.team.getTeamMembers({
        ...(pageParam as TeamApiGetTeamMembersRequest),
        id: teamId,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextPageToken
        ? {
            limit: params.limit,
            nextPageToken: lastPage.nextPageToken,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: !!teamId,
  });

  const teamMembers = data?.pages.flatMap((page) => page.items) || [];

  const teamMembersOptions = useMemo(
    () =>
      teamMembers.map((member) => ({
        label: `${member.firstName} ${member.lastName}`,
        value: member.userId,
      })),
    [teamMembers],
  );

  return {
    teamMembers,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    teamMembersOptions,
  };
};

export const teamMembersCountQuery = ({ teamId }: { teamId: string }) => ({
  queryKey: teamMembersKeys.listCount(teamId),
  queryFn: async () => {
    const response = await MatchingApi.team.getTeamMembers({ id: teamId, limit: 2 });
    return response.data;
  },
  enabled: !!teamId,
});

// Billing
export const useCheckoutSession = (id: string, enabled: boolean) =>
  useQuery<CheckoutView, APIError>({
    queryKey: billingKeys.checkoutSession(id),
    queryFn: async () => {
      const response = await MatchingApi.team.getCheckoutSession({ id });
      return response.data;
    },
    enabled: !!id && enabled,
  });

export const useCustomerPortalUrl = (id: string, enabled: boolean) =>
  useQuery<CustomerPortalView, APIError>({
    queryKey: billingKeys.customerPortalURL(id),
    queryFn: async () => {
      const response = await MatchingApi.team.getCustomerPortalURL({ id });
      return response.data;
    },
    enabled: !!id && enabled,
  });

export const useTeamTrials = (teamId: string) => {
  const { data, isLoading } = useQuery<ListTeamTrialView, APIError>({
    queryKey: teamTrialsKeys.list(teamId),
    queryFn: async () => {
      const response = await MatchingApi.team.getTeamTrials({ id: teamId });
      return response.data;
    },
    enabled: !!teamId,
  });

  return { data, isLoading };
};

export type TeamsSearchableInitialOption = { label: string; value: string };

const TEAMS_LIST_LIMIT = 100;

export const useTeamsList = (
  initialTeamOption?: TeamsSearchableInitialOption,
  enabled: boolean = true,
) => {
  const [searchString, setSearchString] = useState<string | undefined>(undefined);
  const debouncedSearch = useDebounce(searchString, 500);

  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [...teamsKeys.lists(), { searchString: debouncedSearch }] as const,
    initialPageParam: { limit: TEAMS_LIST_LIMIT, searchString: debouncedSearch || undefined },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.team.getTeams(pageParam as TeamApiGetTeamsRequest);
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextPageToken
        ? {
            limit: TEAMS_LIST_LIMIT,
            nextPageToken: lastPage.nextPageToken,
            searchString: debouncedSearch || undefined,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled,
  });

  const teams = data?.pages.flatMap((page) => page.items) || [];

  const teamOptions = useMemo(() => {
    const options = [
      ...teams.map((team) => ({
        label: team.name,
        value: team.id,
      })),
    ];
    return initialTeamOption ? [initialTeamOption, ...options] : options;
  }, [teams]);

  const handleSearchChange = (v?: string) => {
    setSearchString(v || undefined);
  };

  return {
    teams,
    teamOptions,
    isLoading,
    fetchNextPage,
    hasNextPage,
    handleSearchChange,
    searchString,
  };
};

export const useTeamPanels = (params: TeamApiGetTeamPanelsRequest) =>
  useQuery({
    queryKey: teamsKeys.panels(params),
    queryFn: async () => {
      const response = await MatchingApi.team.getTeamPanels(params);
      return response.data;
    },
    enabled: !!params?.id,
  });
